import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { env } from '../config/env';
import { AuthApiService } from '../services/dashboard-api';
import {
  AuthContext as AuthContextType,
  LoginCredentials,
  User,
  UserRole,
} from '../types/auth';

const SSO_ENABLED = env.VITE_SSO_ENABLED;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  /**
   * Check for an existing authenticated session.
   * If SSO is enabled, calls the backend /auth/me endpoint.
   * Falls back to mock user in development when SSO is off.
   */
  const checkSession = async () => {
    if (SSO_ENABLED) {
      try {
        const data = await AuthApiService.getCurrentUser();
        setUser(mapBackendUser(data));
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
      }
    }
    // If no SSO session found (or SSO disabled), user stays null → login page shown
    setIsLoading(false);
  };

  /** Map backend user response to frontend User type */
  function mapBackendUser(data: any): User {
    const roleMap: Record<string, UserRole> = {
      admin: 'Admin',
      leadership: 'Leadership',
      scrum_master: 'ScrumMaster',
      scrummaster: 'ScrumMaster',
      developer: 'Developer',
    };

    const roles: UserRole[] = (data.roles || [])
      .map((r: string) => roleMap[r.toLowerCase()] || r)
      .filter((r: string): r is UserRole =>
        ['Admin', 'Leadership', 'ScrumMaster', 'Developer', 'Guest'].includes(r)
      );

    // Default to Guest if no recognized roles
    if (roles.length === 0) roles.push('Guest');

    return {
      id: data.id,
      email: data.email,
      name: data.displayName || data.name || data.email,
      roles,
      organizationId: data.organizationId || 'default',
      preferences: data.preferences || { theme: 'light', timezone: 'UTC' },
      createdAt: new Date(data.createdAt || Date.now()),
      lastLoginAt: new Date(data.lastLoginAt || Date.now()),
    };
  }

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setIsLoading(true);
    try {
      // Email/password form always does a local mock login in dev
      const mockUser: User = {
        id: '1',
        email: credentials.email,
        name: credentials.email.split('@')[0] || 'Dev User',
        roles: ['Leadership', 'ScrumMaster', 'Developer'],
        organizationId: 'org-1',
        preferences: { theme: 'light', timezone: 'UTC' },
        createdAt: new Date(),
        lastLoginAt: new Date(),
      };
      setUser(mockUser);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.roles.includes(role) ?? false;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
